import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Balance } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { formatBRL, monthName } from '@/lib/format'
import { Button, Card, EmptyRow, Eyebrow, PageHeader, Pill, Spinner } from '@/ui'
import { balancesQuery } from './api'
import { BalanceModal } from './BalanceModal'
import styles from './BalancesView.module.css'

type Editing = { balance?: Balance } | null

export function BalancesView() {
  const { condoId } = useAccess()
  const { data, isPending, isError, refetch } = useQuery(balancesQuery(condoId))
  const [modal, setModal] = useState<Editing>(null)

  return (
    <div>
      <PageHeader
        title="Balancetes"
        subtitle="Publique o resultado do mês e anexe o relatório completo. Os moradores veem os totais e abrem o PDF para o detalhamento."
        actions={
          <Button icon="upload" onClick={() => setModal({})}>
            Enviar balancete
          </Button>
        }
      />
      {isPending ? (
        <div className={styles.center}>
          <Spinner size={24} />
        </div>
      ) : isError ? (
        <div className={styles.center}>
          <p className={styles.error}>Não foi possível carregar os balancetes.</p>
          <Button variant="secondary" size="s" onClick={() => refetch()}>
            Tentar de novo
          </Button>
        </div>
      ) : data.length === 0 ? (
        <Card pad={0} className={styles.empty}>
          <EmptyRow
            icon="payments"
            title="Nenhum balancete publicado"
            description="Envie o primeiro balancete para que os moradores vejam o resultado do mês."
          />
        </Card>
      ) : (
        <div className={styles.grid}>
          {data.map((b) => (
            <BalanceCard key={b.id} b={b} onReplace={() => setModal({ balance: b })} />
          ))}
        </div>
      )}
      {modal && <BalanceModal balance={modal.balance} onClose={() => setModal(null)} />}
    </div>
  )
}

function BalanceCard({ b, onReplace }: { b: Balance; onReplace: () => void }) {
  const result = b.total_income - b.total_expenses
  const label = `${monthName(b.reference_month)} ${b.reference_year}`
  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <div>
          <Eyebrow>Referência</Eyebrow>
          <div className={styles.title}>{label}</div>
        </div>
        <Pill tone={result >= 0 ? 'approved' : 'rejected'}>
          {result >= 0 ? 'Superávit' : 'Déficit'}
        </Pill>
      </div>
      <dl className={styles.totals}>
        <div className={styles.line}>
          <dt>Receitas</dt>
          <dd>{formatBRL(b.total_income)}</dd>
        </div>
        <div className={styles.line}>
          <dt>Despesas</dt>
          <dd>{formatBRL(b.total_expenses)}</dd>
        </div>
        <div className={styles.line}>
          <dt>Resultado</dt>
          <dd className={result >= 0 ? styles.positive : styles.negative}>{formatBRL(result)}</dd>
        </div>
      </dl>
      <div className={styles.buttons}>
        <Button
          size="s"
          variant="secondary"
          icon="description"
          aria-label={`Abrir relatório de ${label}`}
          onClick={() => window.open(b.report_url, '_blank', 'noopener,noreferrer')}
        >
          Abrir relatório
        </Button>
        <Button
          size="s"
          variant="ghost"
          icon="edit"
          aria-label={`Substituir balancete de ${label}`}
          onClick={onReplace}
        >
          Substituir
        </Button>
      </div>
    </Card>
  )
}
